<template>
  <div>
    <loader :loading="loading"></loader>
    <section class="quotewizard">
      <div class="container">
        <br /><br />
        <div
          class="title"
          style="text-align: center; margin: 0px auto; max-width: 700px"
        >
          Get a fast &amp; free Renters Insurance quote
        </div>
        <br /><br />
        <div style="max-width: 600px; margin: 0px auto">
          <status-component />
          <div class="fieldleft">
            <label
              class="tinylabel"
              for="firstname"
              :class="{ validationlabel: hasErrors('firstName') }"
              >First Name <red v-pre>*</red>
            </label>
            <input
              v-model="quote.address.firstName"
              type="text"
              name="firstName"
              :class="{ validationfield: hasErrors('firstName') }"
              @input="validatedFieldChanged"
            />
            <validation-error name="firstName" :errors="validationErrors" />
          </div>
          <div class="fieldright">
            <label
              class="tinylabel"
              :class="{ validationlabel: hasErrors('lastName') }"
            >
              Last Name <red v-pre>*</red>
            </label>
            <input
              v-model="quote.address.lastName"
              type="text"
              name="lastName"
              :class="{ validationfield: hasErrors('lastName') }"
              @input="validatedFieldChanged"
            />
            <validation-error name="lastName" :errors="validationErrors" />
          </div>
          <div style="clear: both"></div>

          <label
            class="tinylabel"
            :class="{ validationlabel: hasErrors('addressSearch') }"
            >Enter the address you'd like to insure
            <red v-if="quote.address.address === ''">*</red>
          </label>
          <form autocomplete="off" @keydown.enter.stop.prevent>
            <vue-google-autocomplete
              id="maps"
              ref="googleAutoComplete"
              name="addressSearch"
              placeholder=""
              country="us"
              :class="{ validationfield: hasErrors('addressSearch') }"
              @placechanged="getAddressData"
              @no-results-found="noAddressResultsFound()"
              @error="noAddressResultsFound()"
            >
            </vue-google-autocomplete>
          </form>
          <validation-error name="addressSearch" :errors="validationErrors" />
          <br /><br /><br />

          <div
            v-if="quote.address.address !== ''"
            class="confirmaddress"
            style="max-width: 600px; margin: 0px auto"
          >
            <strong>Let's make sure your address is correct.</strong>
            <em>Make any changes below.</em>
            <div class="fieldleft">
              <label
                class="tinylabel"
                :class="{ validationlabel: hasErrors('address') }"
                >Street Address <red>*</red>
              </label>
              <input
                v-model.trim="quote.address.address"
                type="text"
                name="address"
                :class="{ validationfield: hasErrors('address') }"
                @input="validatedFieldChanged"
              />
              <validation-error name="address" :errors="validationErrors" />
            </div>
            <div class="fieldright">
              <label class="tinylabel">Apt / Suite </label>
              <input v-model.trim="quote.address.apt" type="text" />
            </div>
            <div class="fieldcol3address">
              <label
                class="tinylabel"
                :class="{ validationlabel: hasErrors('city') }"
                >City <red>*</red></label
              >
              <input
                v-model.trim="quote.address.city"
                type="text"
                name="city"
                :class="{ validationfield: hasErrors('city') }"
                @input="validatedFieldChanged"
              />
              <validation-error name="city" :errors="validationErrors" />
            </div>
            <div class="fieldcol3address middlecol">
              <label
                class="tinylabel"
                :class="{ validationlabel: hasErrors('state') }"
                >State <red>*</red></label
              >
              <us-states-box
                ref="stateBox"
                v-model="quote.address.state"
                :initial="quote.address.state"
                name="state"
                :select-style="{ validationfield: hasErrors('state') }"
                @change="validatedFieldChanged"
              />
              <validation-error name="state" :errors="validationErrors" />
            </div>
            <div class="fieldcol3address lastcol">
              <label
                class="tinylabel"
                :class="{ validationlabel: hasErrors('zip') }"
              >
                Zip <red>*</red>
              </label>
              <input
                v-model.trim="quote.address.zip"
                type="text"
                name="zip"
                :class="{ validationfield: hasErrors('zip') }"
                @input="validatedFieldChanged"
              />
              <validation-error name="zip" :errors="validationErrors" />
            </div>
            <div style="clear: both"></div>
            <br />
          </div>
          <div style="clear: both"></div>
          <br /><br />
          <div style="text-align: center">
            <button :class="{ disabled: !validated }" @click="goToPage">
              Start a quote
            </button>
          </div>
          <br /><br />
        </div>
      </div>
    </section>
  </div>
</template>

<script lang="ts" setup>
import VueGoogleAutocomplete from "./../../../node_modules/vue-google-autocomplete/src/VueGoogleAutocomplete.vue";
import Loader from "@/components/MainLoader.vue";
import UsStatesBox from "@/components/UsStatesBox.vue";
import { InsuranceAddress } from "@/models/InsuranceAddress";
import { User } from "@/models/User";
import ValidationError from "@/components/ValidationError.vue";
import StatusComponent from "@/components/StatusComponent.vue";
import { onMounted, ref, computed, Ref, ComputedRef } from "vue";
import { useQuoteStore } from "@/stores/root/quote";
import { useUserStore } from "@/stores/root/users";
import { useStatusStore } from "@/stores/root/status";
import { stat, scrollTop } from "@/utils/Utils";
import { Namespaces } from "@vue/compiler-core";

// const props = defineProps({
//   anchor: {
//     type: String,
//     default: "",
//   },
//   nextStep: {
//     type: String,
//     default: "",
//   },
// });

const title = "Introduction";
const loading: Ref<boolean> = ref(false);
const quote = useQuoteStore();
const userStore = useUserStore();
const user: User = userStore.user;
const validationErrors: Ref<any[]> = ref([]);
const statusStore = useStatusStore();
const stateBox = ref("");
const googleAutoComplete = ref("");

//Function not used
// function sleep(ms: number) {
//   return new Promise((resolve) => {
//     setTimeout(resolve, ms);
//   });
// }

const validated: ComputedRef<any> = computed(() => {
  return (
    quote.address.firstName &&
    quote.address.lastName &&
    quote.address.address &&
    quote.address.city &&
    quote.address.state.short &&
    quote.address.zip
  );
});

function createValidationMessages(): void {
  validationErrors.value.length = 0;
  if (!quote.address.firstName) {
    const error = {
      name: "firstName",
      value: "First name is required.",
    };
    validationErrors.value.push(error);
  }
  if (!quote.address.lastName) {
    const error = {
      name: "lastName",
      value: "Last name is required.",
    };
    validationErrors.value.push(error);
  }
  if (!quote.address.address) {
    const error = {
      name: "address",
      value: "Address is required.",
    };
    validationErrors.value.push(error);
    const error2 = {
      name: "addressSearch",
      value: "Address is required.",
    };
    validationErrors.value.push(error2);
  }
  if (!quote.address.city) {
    const error = {
      name: "city",
      value: "City is required.",
    };
    validationErrors.value.push(error);
  }
  if (!quote.address.state.short) {
    const error = {
      name: "state",
      value: "State is required.",
    };
    validationErrors.value.push(error);
  }
  if (!quote.address.zip) {
    const error = {
      name: "zip",
      value: "Zip code is required.",
    };
    validationErrors.value.push(error);
  }
}

onMounted(async () => {
  if (user && user.loggedIn && user.firstName && !quote.address.firstName) {
    quote.address.firstName = user.firstName;
  }
  if (user && user.loggedIn && user.lastName && !quote.address.lastName) {
    quote.address.lastName = user.lastName;
  }
});

function getStateFromAddressComponent(place: any) {
  for (let i = 0; i < place.address_components.length; i++) {
    const addressType = place.address_components[i].types[0];

    if (addressType == "administrative_area_level_1") {
      const val = place.address_components[i];
      return {
        short: val["short_name"],
        title: val["long_name"],
      };
    }
  }
  return {
    short: "",
    title: "",
  };
}

/**
 * When the location found
 * @param {Object} addressData Data of the found location
 * @param {Object} placeResultData PlaceResult object
 * @param {String} id Input container ID
 */
function getAddressData(addressData: any, placeResultData: any, id: string) {
  stat(statusStore).clear();
  quote.address.formatted = placeResultData.formatted_address;
  quote.address.zip = addressData.postal_code;

  const state = getStateFromAddressComponent(placeResultData);
  quote.address.state.short = state.short;
  quote.address.state.title = state.title;
  if (stateBox.value) {
    (stateBox.value as any).$forceUpdate();
  }
  quote.address.address =
    (addressData.street_number ? addressData.street_number + " " : "") +
    addressData.route;
  quote.address.city = addressData.locality;
  if (validationErrors.value.length) {
    createValidationMessages();
    if (!validated.value) {
      stat(statusStore).error("Please fill in required fields.");
    }
  }
}

function updateGoogleAutocomplete() {
  const googleAutoCompletes = googleAutoComplete.value as any;
  if (googleAutoCompletes) {
    googleAutoCompletes.update(quote.address.formatted);
  }
}

function noAddressResultsFound() {
  quote.address = new InsuranceAddress();
  updateGoogleAutocomplete();
  stat(statusStore).error("No address has been found. Please try again.");
}

function clearValidationMessage(name: string): void {
  const elements = validationErrors.value.filter((element) => {
    return element.name == name;
  });
  elements.forEach((element) => {
    const index = validationErrors.value.indexOf(element);
    validationErrors.value.splice(index, 1);
  });
  if (!validationErrors.value.length) {
    stat(statusStore).clear();
  }
}

function validatedFieldChanged(event: any): void {
  clearValidationMessage(event.target.name);
}

function hasErrors(name: string): boolean {
  return (
    validationErrors.value.find((element) => {
      return element.name == name;
    }) != null
  );
}
function goToPage() {
  if (!validated.value) {
    createValidationMessages();
    stat(statusStore).error("Please fill in required fields.");
    scrollTop();
    return;
  }
  quote.setQuoteStep("quote-questions");
}
</script>
